import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Link, Clock, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';


import { BACKEND_URL } from '../../config/api';
const API = BACKEND_URL;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function RecordingUploadModal({ 
  isOpen, 
  onClose, 
  liveClass,
  onUploadComplete 
}) {
  const [formData, setFormData] = useState({
    title: liveClass?.title || '',
    description: '',
    recordingUrl: '',
    recordingType: 'youtube'
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateUrl = (url) => {
    if (!url) return false;
    
    const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
    const vimeoRegex = /vimeo\.com\/(?:.*\/)?([0-9]+)/;
    
    if (formData.recordingType === 'youtube') {
      return youtubeRegex.test(url);
    } else if (formData.recordingType === 'vimeo') {
      return vimeoRegex.test(url);
    } else {
      // Basic URL validation for external links
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a title for the recording');
      return;
    }

    if (!formData.recordingUrl.trim()) {
      toast.error('Please enter a recording URL');
      return;
    }
    
    if (!validateUrl(formData.recordingUrl)) {
      toast.error('Please enter a valid URL for the selected platform');
      return;
    }

    setUploading(true);
    setUploadProgress(20);

    try {
      const formDataObj = new FormData();
      formDataObj.append('class_id', liveClass.id);
      formDataObj.append('title', formData.title.trim());
      if (formData.description.trim()) {
        formDataObj.append('description', formData.description.trim());
      }
      formDataObj.append('recording_type', formData.recordingType);
      formDataObj.append('recording_url', formData.recordingUrl.trim());

      setUploadProgress(60);

      const response = await axios.post(
        `${API}/api/class-recordings`,
        formDataObj,
        {
          headers: getAuthHeaders()
        }
      );

      setUploadProgress(100);
      setUploadComplete(true);
      toast.success('Recording uploaded successfully! Students will be notified.');
      
      if (onUploadComplete) {
        onUploadComplete(response.data);
      }

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        resetForm();
      }, 2000);

    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload recording');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: liveClass?.title || '',
      description: '',
      recordingUrl: '',
      recordingType: 'youtube'
    });
    setUploading(false);
    setUploadProgress(0);
    setUploadComplete(false);
  };

  const handleClose = () => {
    if (!uploading) {
      onClose();
      resetForm();
    }
  };

  const handleRemindLater = () => {
    // TODO: Implement remind later functionality
    toast.info('You will be reminded in 6 hours');
    onClose();
  };

  if (!liveClass) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-white rounded-2xl shadow-2xl border-0 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-navy text-center mb-2">
            Class has ended — upload recording
          </DialogTitle>
          <p className="text-[#4D4D4D] text-center">
            Your live class has finished. Please upload the recording or paste a YouTube/Vimeo link so students can watch the session.
          </p>
        </DialogHeader>

        {uploadComplete ? (
          <div className="text-center py-8 space-y-4">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-10 h-10 text-green-500" strokeWidth={2} />
            </div>
            <h3 className="text-xl font-bold text-navy">Upload Complete!</h3>
            <p className="text-[#4D4D4D]">Students will be notified about the recording</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title Field */}
            <div>
              <Label htmlFor="title" className="text-sm font-semibold">Recording Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter recording title..."
                required
                className="mt-1"
              />
            </div>

            {/* Description Field */}
            <div>
              <Label htmlFor="description" className="text-sm font-semibold">Description (Optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Brief description of the class content..."
                className="mt-1"
                rows={3}
              />
            </div>

            {/* Platform Selection */}
            <div>
              <Label htmlFor="platform" className="text-sm font-semibold">Platform</Label>
              <Select 
                value={formData.recordingType} 
                onValueChange={(value) => handleInputChange('recordingType', value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="youtube">YouTube</SelectItem>
                  <SelectItem value="vimeo">Vimeo</SelectItem>
                  <SelectItem value="external_link">Other URL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Recording URL */}
            <div>
              <Label htmlFor="recordingUrl" className="text-sm font-semibold">Recording URL *</Label>
              <Input
                id="recordingUrl"
                type="url"
                value={formData.recordingUrl}
                onChange={(e) => handleInputChange('recordingUrl', e.target.value)}
                placeholder={
                  formData.recordingType === 'youtube' ? 'https://youtube.com/watch?v=...' :
                  formData.recordingType === 'vimeo' ? 'https://vimeo.com/...' :
                  'https://...'
                }
                required
                className="mt-1"
              />
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Processing...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-2" />
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <Button
                type="submit"
                disabled={uploading}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-xl transition-smooth flex items-center justify-center space-x-2"
              >
                <Link className="w-4 h-4" />
                <span>{uploading ? `Processing... ${uploadProgress}%` : 'Upload Recording'}</span>
              </Button>
              
              <Button
                type="button"
                variant="outline"
                onClick={handleRemindLater}
                disabled={uploading}
                className="border-2 border-gray-300 text-[#4D4D4D] hover:bg-gray-50 font-medium py-3 rounded-xl transition-smooth flex items-center space-x-2"
              >
                <Clock className="w-4 h-4" />
                <span>Remind Later</span>
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}